import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { stageLabel, usd } from "../stages";
import { getKey, recordHref, sendInstruction, type Change, type FieldValues, type KeyStatus, type Outcome, type Previous } from "./api";
import { KeyForm, KeyLine } from "./KeyPanel";
import { listen, speechErrorText, speechSupported, type Session } from "./speech";
import "./voice.css";

type KeyView = { phase: "loading" } | { phase: "none" } | { phase: "saved"; status: KeyStatus } | { phase: "error"; message: string };

/**
 * Say (or type) one instruction — "Move Northwind to In Talks, deal is 48k,
 * add their GC Sarah Lee, sarah@northwind.com" — and the CRM makes every
 * change it implies, then lists exactly what it changed, each linked to its
 * record. `onApplied` runs after any change was saved: reload the page's data.
 *
 * Commands run on the signed-in founder's OWN Anthropic key (D-1472): until
 * they have saved one, the bar asks for it instead.
 */
export function VoiceBar({ onApplied }: { onApplied: () => void }) {
  const [key, setKey] = useState<KeyView>({ phase: "loading" });
  const [replacing, setReplacing] = useState(false);
  // Without a key the bar stays one line, so the page's pipeline keeps the top
  // of the screen; the setup form opens on request (or straight away when a
  // command just found the key missing).
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    let live = true;
    getKey().then(
      (r) => { if (live) setKey(r.ok ? (r.status.saved ? { phase: "saved", status: r.status } : { phase: "none" }) : { phase: "error", message: r.message }); },
      (e: unknown) => { if (live) setKey({ phase: "error", message: `Could not reach the CRM: ${e instanceof Error ? e.message : String(e)}` }); },
    );
    return () => { live = false; };
  }, []);

  const saved = (status: KeyStatus) => { setKey(status.saved ? { phase: "saved", status } : { phase: "none" }); setReplacing(false); setSettingUp(false); };

  return (
    <section className="vb card" aria-label="Voice update">
      {key.phase === "loading" ? (
        // Same shape as the no-key row, so the page below does not shift when it resolves.
        <div className="vb-off" aria-busy="true">
          <p className="muted">Checking voice setup…</p>
          <button type="button" className="btn" disabled>Set up voice</button>
        </div>
      ) : null}
      {key.phase === "error" ? <p className="error" role="alert">{key.message}</p> : null}
      {key.phase === "none" && !settingUp ? (
        <div className="vb-off">
          <p className="muted">Voice updates are off: add your own Anthropic API key to turn them on.</p>
          <button type="button" className="btn" onClick={() => setSettingUp(true)}>Set up voice</button>
        </div>
      ) : null}
      {key.phase === "none" && settingUp ? <KeyForm onSaved={saved} onCancel={() => setSettingUp(false)} /> : null}
      {key.phase === "saved" && replacing ? <KeyForm replacing onSaved={saved} onCancel={() => setReplacing(false)} /> : null}
      {key.phase === "saved" && !replacing ? (
        <>
          <Dictation onApplied={onApplied} onNoKey={() => { setKey({ phase: "none" }); setSettingUp(true); }} />
          <KeyLine status={key.status} onReplace={() => setReplacing(true)} onRemoved={saved} />
        </>
      ) : null}
    </section>
  );
}

function Dictation({ onApplied, onNoKey }: { onApplied: () => void; onNoKey: () => void }) {
  const [supported] = useState(speechSupported);
  const [text, setText] = useState("");
  const [interim, setInterim] = useState("");
  const [phase, setPhase] = useState<"idle" | "listening" | "working">("idle");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [previous, setPrevious] = useState<Previous | null>(null);
  const session = useRef<Session | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const hintId = useId();

  useEffect(() => () => session.current?.abort(), []);

  async function submit(raw: string) {
    const instruction = raw.trim();
    if (!instruction) return;
    setPhase("working");
    setOutcome(null);
    let result: Outcome;
    try {
      result = await sendInstruction(instruction, previous);
    } catch (e) {
      result = { ok: false, code: "network", message: `Could not reach the assistant: ${e instanceof Error ? e.message : String(e)}`, changes: [] };
    }
    if (!result.ok && result.code === "no_key") {
      onNoKey();
      return;
    }
    setOutcome(result);
    setPhase("idle");
    // A follow-up question keeps the whole exchange so far, so the next answer is read in context.
    const asked = previous ? `${previous.text} (asked "${previous.question}", answered "${instruction}")` : instruction;
    setPrevious(result.ok && result.question ? { text: asked.slice(0, 2000), question: result.question } : null);
    if (result.ok) setText("");
    if (result.changes.length > 0) onApplied();
    input.current?.focus();
  }

  function startListening() {
    setSpeechError(null);
    setOutcome(null);
    setText("");
    setInterim("");
    setPhase("listening");
    session.current = listen({
      onTranscript: (finalText, interimText) => { setText(finalText); setInterim(interimText); },
      onError: (code) => setSpeechError(speechErrorText(code)),
      onEnd: (finalText) => {
        session.current = null;
        setInterim("");
        setText(finalText);
        if (finalText) void submit(finalText);
        else setPhase("idle");
      },
    });
  }

  function toggleMic() {
    if (phase === "listening") session.current?.stop();
    else startListening();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (phase === "idle") void submit(text);
  }

  const listening = phase === "listening";
  const shown = listening && interim ? `${text}${text ? " " : ""}${interim}` : text;
  const status = listening ? "Listening… press the mic again or pause to finish." : phase === "working" ? "Working…" : "";

  return (
    <>
      <form className="vb-row" onSubmit={onSubmit}>
        {supported ? (
          <button
            type="button"
            className={`vb-mic${listening ? " on" : ""}`}
            aria-pressed={listening}
            aria-label={listening ? "Stop dictation" : "Start dictation"}
            disabled={phase === "working"}
            onClick={toggleMic}
          >
            <MicIcon />
          </button>
        ) : null}
        <label className="vb-sr" htmlFor={inputId}>{previous ? "Your answer" : "Instruction"}</label>
        <input
          id={inputId}
          ref={input}
          className="vb-input"
          value={shown}
          readOnly={listening}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape" && listening) session.current?.abort(); }}
          placeholder={previous ? "Answer the question…" : supported ? "Say or type: Move Northwind to In Talks, deal is 48k" : "Type: Move Northwind to In Talks, deal is 48k"}
          aria-describedby={hintId}
          autoComplete="off"
          maxLength={2000}
        />
        <button type="submit" className="btn primary" disabled={phase !== "idle" || !text.trim()}>
          {phase === "working" ? "Working…" : "Apply"}
        </button>
      </form>

      <p id={hintId} className="muted small vb-hint">
        {supported
          ? "Your browser's speech service transcribes what you say (in Chrome, audio is sent to a Google server). The text then goes to Claude, on your own Anthropic key, which edits the CRM as you."
          : "Voice input is not available in this browser (Firefox, for example), so type the instruction instead. Claude reads it, on your own Anthropic key, and edits the CRM as you."}
      </p>

      <div role="status" aria-live="polite" className="vb-status">
        {status ? <p className="muted">{status}</p> : null}
      </div>
      {speechError ? <p className="error" role="alert">{speechError}</p> : null}
      {outcome ? <Result outcome={outcome} pending={previous} onStartOver={() => { setPrevious(null); setOutcome(null); }} /> : null}
    </>
  );
}

function Result({ outcome, pending, onStartOver }: { outcome: Outcome; pending: Previous | null; onStartOver: () => void }) {
  return (
    <div className="vb-result">
      {outcome.ok ? (
        <>
          {outcome.question ? (
            <div className="vb-question" role="alert">
              <p><strong>Question:</strong> {outcome.question}</p>
              {pending ? (
                <p className="muted small">
                  Answer it above, or <button type="button" className="vb-link" onClick={onStartOver}>start over</button>.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="vb-summary">{outcome.summary}</p>
          )}
        </>
      ) : (
        <p className="error" role="alert">{outcome.message}</p>
      )}
      {outcome.changes.length > 0 ? (
        <>
          <h3>{outcome.ok ? "Changed" : "Changed before it stopped"}</h3>
          <ul className="vb-changes" aria-label="Changes made">
            {outcome.changes.map((c, i) => <ChangeItem key={`${c.entity_type}-${c.id}-${i}`} change={c} />)}
          </ul>
        </>
      ) : outcome.ok && !outcome.question ? (
        <p className="muted small">No records were changed.</p>
      ) : null}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  name: "Name", stage: "Stage", deal_value: "Deal value", owner: "Owner", website: "Website", industry: "Industry",
  first_name: "First name", last_name: "Last name", title: "Title", email: "Email", phone: "Phone",
  linkedin_url: "LinkedIn", company: "Company", notes_added: "Note added",
};

function show(field: string, v: string | number | null | undefined): string {
  if (v == null || v === "") return "—";
  if (field === "stage") return stageLabel(String(v));
  if (field === "deal_value") return usd(Number(v));
  return String(v);
}

function ChangeItem({ change }: { change: Change }) {
  const fields = Object.keys(change.after).filter((k) => change.kind === "updated" || (change.after[k] != null && change.after[k] !== ""));
  const what = change.entity_type === "company" ? "company" : "person";
  return (
    <li>
      <p>
        <a href={recordHref(change)}>{change.label}</a>{" "}
        <span className="muted small">{change.kind === "created" ? `new ${what}` : `${what} updated`}</span>
      </p>
      <dl className="vb-fields">
        {fields.map((f) => <Field key={f} field={f} before={change.before} after={change.after} kind={change.kind} />)}
      </dl>
    </li>
  );
}

function Field({ field, before, after, kind }: { field: string; before: FieldValues | null; after: FieldValues; kind: Change["kind"] }) {
  return (
    <div className="vb-field">
      <dt>{FIELD_LABELS[field] ?? field}</dt>
      <dd>
        {kind === "updated" && field !== "notes_added" ? (
          <><span className="vb-before">{show(field, before?.[field])}</span> → <strong>{show(field, after[field])}</strong></>
        ) : (
          <strong>{show(field, after[field])}</strong>
        )}
      </dd>
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-2.07A7 7 0 0 0 19 12h-2Z" />
    </svg>
  );
}
